/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import type {
  Editor,
  InputDescription,
  IntegrationDescription,
  Member,
} from "@smartsuite/automation-engine-api";
import { ValueDescription_T } from "@smartsuite/automation-engine-api";
import {
  AvatarMenuOption,
  DropdownPanel,
  MenuGroupContent,
  MenuLineSeparator,
  MenuOption,
  MenuOptionWrapper,
  Paragraph,
  PickerInputField,
  Text,
} from "@smartsuite/react-ui/lib";
import { observer } from "mobx-react-lite";
import type { MutableRefObject, Ref } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as R from "remeda";
import { CaptionBlock } from "../../../../../components/CaptionBlock";
import { useRootStore } from "../../../../../stores/hooks";
import { checkInputValidation } from "../../../../../stores/automation/utils/inputValidation";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../types/candidate";
import { getLocalCssColorVariables } from "../../../../../utils/cssVariablesUtils";
import { nameToInitials } from "../utils";
import { Root, StyledNoFields, StyledReactInputFieldContainer } from "./style";
import type { EmailPill, TriggerActionOption } from "./utils";
import {
  ejectPillFromInput,
  handleActionOutput,
  handleTriggerOutput,
  mergeNextPillToInput,
  triggerToOptions,
  useConverters,
} from "./utils";
import {
  emailContextSpecificFields,
  generalTypes,
  ssFields,
  ssTypes,
} from "./string-output-types";
import {
  ActionLabel,
  MENU_MAX_HEIGHT,
  MENU_MAX_HEIGHT_MARGIN,
} from "../../../../../utils/constants";
import { POSITION_AT, POSITION_MY } from "../style";
import type { ActionOptionGroup } from "../utils/action-options";
import { actionsToOptions } from "../utils/action-options";
import { FieldMissingError } from "../../../Sections/ErrorSection";
import { useFieldMissingValidation } from "../../../../../stores/automation/hooks/field-missing-validation";
import { UpdateValueDropdown } from "../FieldEditor/FieldEditorRow/containers/UpdateValueDropdown";
import { SolutionColorVariables } from "@smartsuite/types";
import { CurrentOption } from "../FieldEditor/FieldEditorRow/containers/CurrentOption";
import { ifLoopingSourceIsActionFind } from "../FieldEditor/FieldEditorRow/fields/utils";
import { useLoopingMenu } from "../hooks/use-looping-menu";
import { useWorkspaceMemberDirectory } from "../../../../../hooks/use-member-directory";
import { KeyCode } from "@smartsuite/react-ui/lib/constants/keycode";
import { toJS } from "mobx";
import {
  actionSourceBoundary,
  canOfferActionSources,
  resolveEditorLoopScope,
} from "../utils/action-sources";
import type { LoopScope } from "../../../../../stores/automation/looping.store";

export const EmailEditor = observer<{
  allowManual?: boolean;
  description?: InputDescription;
  editor: Editor;
  config: InputCandidate;
  trigger: TriggerCandidate;
  onChange: (input: InputCandidate) => void;
  showValidationError: boolean;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  /** Set by ArgumentEditor when `actions` is an explicit, already-scoped list
   * (Conditional Logic branch conditions): offer it whole even though there is
   * no owning `action` to bound it (BUG-21770). */
  actionsArePreScoped?: boolean;
  /** The loop whose Current Item this editor may offer, used verbatim when
   * `actionsArePreScoped` is set. See useLoopingMenu (BUG-21770). */
  loopScope?: LoopScope;
  integrations: IntegrationDescription[];
  isMultiple: boolean;
  isError?: boolean;
  onClickTrash?: () => void;
  showTeams?: boolean;
}>(
  ({
    allowManual = true,
    description,
    editor,
    config,
    trigger,
    onChange,
    showValidationError,
    actions,
    action,
    actionsArePreScoped,
    loopScope,
    integrations,
    isMultiple,
    isError,
    onClickTrash,
    showTeams = true,
  }) => {
    const { t } = useTranslation();
    const store = useRootStore();

    // Recipients are addressed directly rather than through a record, so any
    // account member is a legitimate target.
    const { members, teams } = useWorkspaceMemberDirectory();

    const [isOpen, setIsOpen] = useState(false);
    const [tab, setTab] = useState<
      "root" | "fields" | "members" | "actions" | "teams" | "current"
    >("fields");
    const [triggerFields, setTriggerFields] = useState<TriggerActionOption[]>(
      [],
    );
    const [actionFields, setActionFields] = useState<ActionOptionGroup[]>([]);
    const [selectedAction, setSelectedAction] = useState<
      ActionOptionGroup | undefined
    >();
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [validationError, setValidationError] = useState<string>("");
    const [focus, setFocus] = useState<boolean>(false);
    const buttonRef = useRef<HTMLButtonElement>();
    const [menuHeight, setMenuHeight] = useState<number>(MENU_MAX_HEIGHT);
    const [isCleanAction, setIsCleanAction] = useState<boolean>(false);
    const [actionType, setActionType] = useState<
      | "replace"
      | "clear"
      | "append"
      | "mathAdd"
      | "mathSubtract"
      | "mathMultiply"
      | "mathDivide"
      | undefined
    >(config.operatorConfig?.operator?.oneofKind);
    const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
      useFieldMissingValidation();
    const editorWrapperId = `${config.inputId}-editor-wrapper`;
    const rootLooping = store.automations.candidate.looping;
    const looping = resolveEditorLoopScope(
      action,
      rootLooping,
      loopScope,
      actionsArePreScoped,
    );
    const { convertInputToPills } = useConverters();

    const { loopingFields } = useLoopingMenu({
      loopScope,
      actionsArePreScoped,
      trigger,
      actions,
      integrations,
      types: [ValueDescription_T.EMAIL, ValueDescription_T.MEMBER],
      ssFieldTypes: ssFields,
      systemAllowedOptions: [],
      action,
    });

    useEffect(() => {
      if (window.innerHeight < menuHeight * 2 + MENU_MAX_HEIGHT_MARGIN) {
        setMenuHeight(window.innerHeight / 2 - MENU_MAX_HEIGHT_MARGIN);
      } else {
        setMenuHeight(MENU_MAX_HEIGHT);
      }
      window.addEventListener("resize", handleResize, false);

      return () => {
        window.removeEventListener("resize", handleResize, false);
      };
    }, []);

    const handleResize = (): void => {
      if (window.innerHeight < menuHeight * 2 + MENU_MAX_HEIGHT_MARGIN) {
        setMenuHeight(window.innerHeight / 2 - MENU_MAX_HEIGHT_MARGIN);
      } else {
        setMenuHeight(MENU_MAX_HEIGHT);
      }
    };

    useEffect(() => {
      if (showValidationError && R.isTruthy(description)) {
        setValidationError(checkInputValidation(description, config ?? []));
      } else {
        setValidationError("");
      }
    }, [config, description, showValidationError]);

    useEffect(() => {
      setTriggerFields(
        triggerToOptions(
          trigger,
          generalTypes,
          ssTypes,
          ssFields,
          emailContextSpecificFields,
        ),
      );
    }, [trigger]);

    useEffect(() => {
      if (triggerFields.length) {
        setTab("fields");
      } else if (members.length) {
        setTab("members");
      } else {
        setTab("root");
      }
    }, [triggerFields, members]);

    useEffect(() => {
      if (canOfferActionSources(action, actionsArePreScoped)) {
        setActionFields(
          actionsToOptions(
            actions,
            actionSourceBoundary(action, actionsArePreScoped),
            integrations,
            [ValueDescription_T.EMAIL, ValueDescription_T.MEMBER],
            ssFields,
            [],
            looping,
          ),
        );
      }
    }, [actions, action, integrations, actionsArePreScoped, loopScope]);

    useEffect(() => {
      if (store.inputToFocus?.inputId === config.inputId) {
        setIsOpen(true);
        const focusTimeoutId = setTimeout(() => {
          const wrapper = document.getElementById(editorWrapperId);
          const editor = wrapper?.querySelector(".picker-input-field__input");
          // @ts-expect-error
          editor?.focus();
          store.inputToFocus = undefined;
        });
        return () => clearTimeout(focusTimeoutId);
      }
    }, []);

    useEffect(() => {
      if (actionType === undefined) return;
      if (actionType === "clear") {
        setIsOpen(false);
        setIsCleanAction(true);
      } else {
        setIsCleanAction(false);
      }
      onChange({
        ...config,
        value:
          actionType === "clear"
            ? {
                values: [],
              }
            : config.value,
        operatorConfig: {
          // @ts-expect-error
          operator: {
            oneofKind: actionType,
            [actionType]: {},
          },
        },
      });
    }, [actionType, isOpen]);

    const pillList = useMemo(() => {
      return convertInputToPills(
        config,
        members,
        teams,
        triggerFields,
        actionFields,
        trigger,
        actions,
        action,
        loopingFields,
      );
    }, [config, members, teams, triggerFields, actionFields]);

    const isEmailValid = useMemo(() => getEmailValidation(editor), [editor]);

    const isFieldError = (pills: EmailPill[]): boolean =>
      Boolean(pills.find((item) => item.isError));

    const addCustomEmail = (email: string): void => {
      setIsOpen(false);
      onChange(
        mergeNextPillToInput(
          config,
          {
            type: "custom",
            email,
            isEmailValid,
          },
          isNextPillAllowed(config, isMultiple),
        ),
      );
    };

    const isNextPillAllowed = (
      config: InputCandidate,
      isMultiple: boolean,
    ): boolean => {
      if (!isMultiple && config.value?.values.length === 1) {
        if (focus) {
          setFocus(false);
        }
        return false;
      } else {
        return true;
      }
    };

    const unselectedTriggerFields = triggerFields
      .filter(
        (option) =>
          !pillList.some(
            (pill) =>
              pill.type === "triggerOutput" && pill.value === option.outputId,
          ),
      )
      .filter((option) =>
        option.title.toLowerCase().includes(searchQuery.toLowerCase()),
      );

    const availableMembers = members
      .filter(
        (member) =>
          !pillList.some(
            (pill) => pill.type === "member" && pill.value === member.memberId,
          ),
      )
      .filter((member) =>
        getMemberName(member).toLowerCase().includes(searchQuery.toLowerCase()),
      );

    const changeActionType = (
      type:
        | "replace"
        | "clear"
        | "append"
        | "mathAdd"
        | "mathSubtract"
        | "mathMultiply"
        | "mathDivide"
        | undefined,
    ): void => {
      setActionType(type);
    };

    const hasError =
      Boolean(isError) ||
      isFieldError(pillList) ||
      validationError !== "" ||
      !isInputValidByFieldMissing(config, action);

    return (
      <Root>
        <StyledReactInputFieldContainer id={editorWrapperId}>
          {description !== undefined && (
            <CaptionBlock
              description={description}
              onClickTrash={onClickTrash}
            />
          )}
          <PickerInputField
            showAddButton={!isCleanAction}
            className={[
              isError ? "is-error" : "",
              hasError ? "pill-picker-error" : "",
              isCleanAction ? "is-clear" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onChangeFocused={(isFocused, event) => {
              setFocus(isFocused);

              if (
                description?.editor?.hidePopupOnHover !== true &&
                isFocused &&
                event?.target.className !== "field-type-pill__remove" &&
                event?.target.className !==
                  "icon-button icon-button--kind-circle input-picker-group__plus-button"
              ) {
                setIsOpen(true);
              }
            }}
            onInputChange={() => {
              setIsOpen(false);
            }}
            state={hasError ? "error" : isOpen || focus ? "active" : "default"}
            // @ts-expect-error
            buttonRef={buttonRef as Ref<HTMLButtonElement>}
            isOpen={isOpen}
            pills={pillList}
            pillStyle={{
              textSize: "m",
            }}
            onButtonClick={() => setIsOpen((open) => !open)}
            onPillRemove={(index) => {
              setIsOpen(false);
              onChange(ejectPillFromInput(config, index));
            }}
            allowManualInput={
              !isCleanAction &&
              allowManual &&
              isNextPillAllowed(config, isMultiple)
            }
            onInputAdd={addCustomEmail}
            placeholder={
              isCleanAction
                ? t("automation-form.clean-operator-editor-placeholder")
                : isOpen || focus
                  ? pillList.length > 0
                    ? ""
                    : allowManual
                      ? t("editor.type-or-use-field")
                      : t("editor.use-field")
                  : ""
            }
            keyDownCodes={
              getValidation(editor) === undefined
                ? [KeyCode.tab, KeyCode.enter]
                : [KeyCode.tab, KeyCode.enter, KeyCode.space]
            } // When there is no validation, allow space in manual input
          >
            {action?.actionReference?.actionId === ActionLabel.updateRecord &&
              description?.operators?.operators !== undefined &&
              description?.operators?.operators.length > 0 && (
                <UpdateValueDropdown
                  operatorConfig={config.operatorConfig}
                  operatorDescriptions={description?.operators.operators}
                  onOpenedChange={setFocus}
                  onChange={changeActionType}
                />
              )}

            {isOpen && (
              <DropdownPanel
                targetElementRef={buttonRef as MutableRefObject<HTMLElement>}
                clickOutsideExcept={[".dropdown-panel"]}
                isActive={isOpen}
                at={POSITION_AT}
                my={POSITION_MY}
                minWidth={300}
                arrowBackground="custom"
                arrowCustomBackground={SolutionColorVariables.sc1}
                panelLineBackground={SolutionColorVariables.sc1}
                portalStyles={getLocalCssColorVariables(store.theme)}
                onClickOutside={() => setIsOpen(false)}
                onCloseAnimationEnd={() => {
                  setSearchQuery("");
                  setIsOpen(false);
                }}
              >
                {tab === "root" && (
                  <MenuOptionWrapper>
                    {unselectedTriggerFields.length > 0 && (
                      <MenuOption
                        submenuArrow="right"
                        onClick={() => setTab("fields")}
                      >
                        {t("automation-form.fields-from-trigger")}
                      </MenuOption>
                    )}
                    {actionFields.map(
                      (actionMenu) =>
                        actionMenu?.options.length > 0 && (
                          <MenuOption
                            key={actionMenu.action.hash}
                            submenuArrow="right"
                            onClick={() => {
                              setSelectedAction(actionMenu);
                              setTab("actions");
                            }}
                          >
                            {actionMenu.hideNamePrefix === true
                              ? actionMenu.name
                              : t("field-popups.action-popup", {
                                  name: actionMenu.name,
                                })}
                          </MenuOption>
                        ),
                    )}
                    {members.length > 0 && (
                      <MenuOption
                        submenuArrow="right"
                        onClick={() => setTab("members")}
                      >
                        {t("editor.members")}
                      </MenuOption>
                    )}
                    {showTeams && teams.length > 0 && (
                      <MenuOption
                        submenuArrow="right"
                        onClick={() => setTab("teams")}
                      >
                        {t("editor.teams")}
                      </MenuOption>
                    )}
                    <CurrentOption
                      loopingMenu={loopingFields}
                      loopScope={looping}
                      actionsArePreScoped={actionsArePreScoped}
                      addPill={(looping) => {
                        // Case when source for looping is the find action
                        if (
                          ifLoopingSourceIsActionFind(looping) ||
                          loopingFields?.options?.length
                        ) {
                          setTab("current");
                          return;
                        }

                        if (looping.source) {
                          onChange(
                            mergeNextPillToInput(
                              config,
                              {
                                type: "loop",
                                looping,
                              },
                              isNextPillAllowed(config, isMultiple),
                            ),
                          );
                        }
                      }}
                    />
                  </MenuOptionWrapper>
                )}

                {tab === "fields" && (
                  <MenuGroupContent
                    searchPlaceholder={t("automation-form.search-fields")}
                    maxHeight={menuHeight}
                    title={t("automation-form.fields-from-trigger")}
                    onSearchChange={(query) => setSearchQuery(query)}
                    onNavigateBack={() => {
                      setTab("root");
                      setSearchQuery("");
                    }}
                  >
                    {unselectedTriggerFields
                      .filter((option) => !option.contextSpecific)
                      .map((option) => {
                        return (
                          <MenuOption
                            key={option.outputId}
                            onClick={() => {
                              setTimeout(() => {
                                setIsOpen(isMultiple);
                              });
                              if (R.isTruthy(trigger.triggerReference)) {
                                onChange(
                                  mergeNextPillToInput(
                                    config,
                                    {
                                      type: "triggerOutput",
                                      triggerReference:
                                        trigger.triggerReference,
                                      outputId: option.outputId,
                                    },
                                    isNextPillAllowed(config, isMultiple),
                                  ),
                                );
                              }
                            }}
                            iconName={option.icon}
                            iconSize="S"
                          >
                            {option.title}
                          </MenuOption>
                        );
                      })}

                    {unselectedTriggerFields.filter(
                      (option) => !option.contextSpecific,
                    ).length > 0 &&
                      unselectedTriggerFields.filter(
                        (option) => option.contextSpecific,
                      ).length > 0 && <MenuLineSeparator />}

                    {unselectedTriggerFields
                      .filter((option) => option.contextSpecific)
                      .map((option) => {
                        return (
                          <MenuOption
                            key={option.outputId}
                            onClick={() => {
                              setTimeout(() => {
                                setIsOpen(isMultiple);
                              });
                              if (R.isTruthy(trigger.triggerReference)) {
                                onChange(
                                  mergeNextPillToInput(
                                    config,
                                    {
                                      type: "triggerOutput",
                                      triggerReference:
                                        trigger.triggerReference,
                                      outputId: option.outputId,
                                    },
                                    isNextPillAllowed(config, isMultiple),
                                  ),
                                );
                              }
                            }}
                            iconName={option.icon}
                            iconSize="S"
                          >
                            {option.title}
                          </MenuOption>
                        );
                      })}

                    {unselectedTriggerFields.length < 1 && (
                      <StyledNoFields>No results</StyledNoFields>
                    )}
                  </MenuGroupContent>
                )}

                {tab === "members" && (
                  <MenuGroupContent
                    title={t("editor.members")}
                    onSearchChange={(query) => setSearchQuery(query)}
                    onNavigateBack={() => {
                      setTab("root");
                      setSearchQuery("");
                    }}
                  >
                    {availableMembers.map((member) => (
                      <AvatarMenuOption
                        key={member.memberId}
                        onClick={() => {
                          onChange(
                            mergeNextPillToInput(
                              config,
                              {
                                type: "member",
                                memberId: member.memberId,
                                memberStatus: member.status,
                              },
                              isNextPillAllowed(config, isMultiple),
                            ),
                          );
                        }}
                        initials={nameToInitials(getMemberName(member))}
                        image={member.profileImageUrl}
                      >
                        {getMemberName(member)}
                      </AvatarMenuOption>
                    ))}
                    {availableMembers.length === 0 && (
                      <StyledNoFields>No results</StyledNoFields>
                    )}
                  </MenuGroupContent>
                )}
                {tab === "teams" && showTeams && (
                  <MenuGroupContent
                    title={t("editor.teams")}
                    onSearchChange={(query) => setSearchQuery(query)}
                    onNavigateBack={() => {
                      setTab("root");
                      setSearchQuery("");
                    }}
                  >
                    {teams
                      .filter(
                        (team) =>
                          !pillList.some(
                            (pill) =>
                              pill.type === "team" &&
                              pill.value === team.teamId,
                          ),
                      )
                      .filter((team) =>
                        team.name
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                      )
                      .map((team) => (
                        <AvatarMenuOption
                          key={team.teamId}
                          onClick={() => {
                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            onChange(
                              mergeNextPillToInput(
                                config,
                                {
                                  type: "team",
                                  teamId: team.teamId,
                                },
                                isNextPillAllowed(config, isMultiple),
                              ),
                            );
                          }}
                          initials={nameToInitials(team.name)}
                        >
                          {team.name}
                        </AvatarMenuOption>
                      ))}
                  </MenuGroupContent>
                )}
                {tab === "current" && loopingFields !== undefined && (
                  <MenuGroupContent
                    title={t("general.current-item")}
                    onSearchChange={(query) => setSearchQuery(query)}
                    onNavigateBack={() => {
                      setTab("root");
                      setSearchQuery("");
                    }}
                  >
                    {loopingFields.options
                      .filter(
                        (option) =>
                          !pillList.some(
                            (pill) =>
                              pill.type === "actionOutput" &&
                              pill.value === option.outputId,
                          ),
                      )
                      .filter((option) => !option.contextSpecific)
                      .filter((option) =>
                        option.title
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                      )
                      .map((option) => (
                        <MenuOption
                          key={option.outputId}
                          onClick={() => {
                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            const actionConfig =
                              looping === undefined
                                ? undefined
                                : handleActionOutput(looping, option);
                            if (actionConfig) {
                              onChange(
                                mergeNextPillToInput(
                                  config,
                                  actionConfig,
                                  isNextPillAllowed(config, isMultiple),
                                ),
                              );
                              return;
                            }

                            const triggerConfig = handleTriggerOutput(
                              trigger,
                              option,
                            );
                            if (triggerConfig) {
                              onChange(
                                mergeNextPillToInput(
                                  config,
                                  triggerConfig,
                                  isNextPillAllowed(config, isMultiple),
                                ),
                              );
                            }
                          }}
                          iconName={option.icon}
                          iconSize="S"
                        >
                          <Text styles={{ marginLeft: "13px" }}>
                            {option.title}
                          </Text>
                        </MenuOption>
                      ))}
                  </MenuGroupContent>
                )}
                {tab === "actions" && selectedAction !== undefined && (
                  <MenuGroupContent
                    title={t("field-popups.action-popup", {
                      name: selectedAction.name,
                    })}
                    onSearchChange={(query) => setSearchQuery(query)}
                    onNavigateBack={() => {
                      setTab("root");
                      setSearchQuery("");
                    }}
                  >
                    {selectedAction.options
                      .filter(
                        (option) =>
                          !pillList.some(
                            (pill) =>
                              pill.type === "actionOutput" &&
                              pill.value === option.outputId,
                          ),
                      )
                      .filter((option) => !option.contextSpecific)
                      .filter((option) =>
                        option.title
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                      )
                      .map((option) => (
                        <MenuOption
                          key={option.outputId}
                          onClick={() => {
                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            if (
                              R.isTruthy(selectedAction.action.actionReference)
                            ) {
                              onChange(
                                mergeNextPillToInput(
                                  config,
                                  {
                                    type: "actionOutput",
                                    actionReference:
                                      selectedAction.action.actionReference,
                                    outputId: option.outputId,
                                  },
                                  isNextPillAllowed(config, isMultiple),
                                ),
                              );
                            }
                          }}
                          iconName={option.icon}
                          iconSize="S"
                        >
                          <Text styles={{ marginLeft: "13px" }}>
                            {option.title}
                          </Text>
                        </MenuOption>
                      ))}

                    {selectedAction.options.filter(
                      (option) => !option.contextSpecific,
                    ).length > 0 &&
                      selectedAction.options.filter(
                        (option) => option.contextSpecific,
                      ).length > 0 && <MenuLineSeparator />}

                    {selectedAction.options
                      .filter(
                        (option) =>
                          !pillList.some(
                            (pill) =>
                              pill.type === "actionOutput" &&
                              pill.value === option.outputId,
                          ),
                      )
                      .filter((option) => option.contextSpecific)
                      .filter((option) =>
                        option.title
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                      )
                      .map((option) => (
                        <MenuOption
                          key={option.outputId}
                          onClick={() => {
                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            if (
                              R.isTruthy(selectedAction.action.actionReference)
                            ) {
                              onChange(
                                mergeNextPillToInput(
                                  config,
                                  {
                                    type: "actionOutput",
                                    actionReference:
                                      selectedAction.action.actionReference,
                                    outputId: option.outputId,
                                  },
                                  isNextPillAllowed(config, isMultiple),
                                ),
                              );
                            }
                          }}
                          iconName={option.icon}
                          iconSize="S"
                        >
                          <Text styles={{ marginLeft: "13px" }}>
                            {option.title}
                          </Text>
                        </MenuOption>
                      ))}
                  </MenuGroupContent>
                )}
              </DropdownPanel>
            )}
          </PickerInputField>
          {validationError !== "" && (
            <Paragraph color={"error"} size={"xs"}>
              {validationError}
            </Paragraph>
          )}
          <FieldMissingError
            message={getFieldMissingErrorMessage(config, action)}
          />
        </StyledReactInputFieldContainer>
      </Root>
    );
  },
);
EmailEditor.displayName = "EmailEditor";

const getMemberName = (member: Member): string =>
  member.name.length > 0 ? member.name : (member.email[0] ?? "");

const getEmailValidation = (editor: Editor): ((email: string) => boolean) => {
  if (editor?.validation.oneofKind === "regex") {
    const { regex } = editor.validation;

    return (email: string) => RegExp(regex).test(email.toLowerCase());
  } else {
    return (_: string) => true;
  }
};

const getValidation = (editor: Editor): Editor["validation"]["oneofKind"] => {
  return editor.validation.oneofKind;
};
